import { createRoot, type Root } from "react-dom/client"
import {
  applyThemeVars,
  createAnchoredShadowHost,
  createModalShadowHost,
  FADE_MS,
  prefersReducedMotion,
  type ModalShadowHost,
} from "../mount/createShadowHost"
import { lockPageScroll } from "../mount/lockPageScroll"
import { openResult, reserveNewTab } from "../lib/openResult"
import { resolvePlaceholder } from "../config"
import type { BppSearchConfig, EmbedUi, PropertyTab, SearchMode } from "../types"
import { PropertyAutocomplete } from "./PropertyAutocomplete"
import { AgentAutocomplete } from "./AgentAutocomplete"

function isClickInsideModal(event: MouseEvent): boolean {
  return event
    .composedPath()
    .some(
      (node) =>
        node instanceof Element &&
        (node.classList.contains("bpp-modal") ||
          node.classList.contains("bpp-dropdown") ||
          node.classList.contains("bpp-search-modal-root"))
    )
}

function entityTypeForMode(mode: SearchMode): "agent" | "office" {
  return mode === "office" ? "office" : "agent"
}

export type OpenModalOptions = {
  mode: SearchMode
  defaultTab?: PropertyTab
  tabs?: PropertyTab[]
  ui?: EmbedUi
  placeholder?: string
  zIndex?: number
}

type ActiveModal = {
  host: ModalShadowHost
  root: Root
  triggerElement: HTMLElement | null
}

export class ModalController {
  private active: ActiveModal | null = null
  private releaseScrollLock: (() => void) | null = null
  private reservedTab: Window | null = null
  /** Bumped on every close so an in-flight result write cannot navigate afterward. */
  private epoch = 0

  constructor(
    private readonly config: BppSearchConfig,
    private readonly onRedirectCallback?: (href: string) => void
  ) {}

  /**
   * Call in the click turn, before any await. Returns a check that goes false
   * once this surface closes, so the await's continuation does not navigate.
   * Reserves a blank tab when `openInNewTab` is set.
   */
  prepareNewTab = (): (() => boolean) => {
    const epoch = this.epoch
    if (this.config.openInNewTab === true && (!this.reservedTab || this.reservedTab.closed)) {
      this.reservedTab = reserveNewTab()
    }
    return () => epoch === this.epoch
  }

  /** Shared by modal and inline dropdown surfaces so callbacks fire once, everywhere. */
  redirect = (href: string): void => {
    this.config.onSelect?.(href)
    this.config.onRedirect?.(href)
    this.onRedirectCallback?.(href)
    const reserved = this.reservedTab
    this.reservedTab = null
    const destination = openResult(href, this.config.openInNewTab === true, reserved)
    if (destination === "new-tab") {
      this.close()
    }
  }

  open(options: OpenModalOptions, triggerElement: HTMLElement | null): void {
    this.close(true)

    const zIndex = options.zIndex ?? this.config.zIndex ?? 999999
    const variant = options.ui ?? "lightbox"
    const shadowHost =
      variant === "dropdown" && triggerElement
        ? createAnchoredShadowHost(zIndex, triggerElement)
        : createModalShadowHost(zIndex)
    const { host: backdrop, mount } = shadowHost
    applyThemeVars(mount, this.config.theme)

    const handleClose = () => {
      this.close()
      // Focusing the trigger scrolls it into view and jumps the page.
      triggerElement?.focus({ preventScroll: true })
    }

    const handleRedirect = this.redirect

    const root = createRoot(mount)
    this.config.onOpen?.()

    if (options.mode === "agent" || options.mode === "office") {
      const entityType = entityTypeForMode(options.mode)
      root.render(
        <AgentAutocomplete
          searchBaseUrl={this.config.searchBaseUrl}
          entityType={entityType}
          variant={variant}
          placeholder={resolvePlaceholder(
            options.mode,
            undefined,
            options.placeholder,
            this.config.placeholders
          )}
          onClose={handleClose}
          onRedirect={handleRedirect}
        />
      )
    } else {
      root.render(
        <PropertyAutocomplete
          searchBaseUrl={this.config.searchBaseUrl}
          defaultTab={options.defaultTab ?? "buy"}
          tabs={options.tabs}
          variant={variant}
          placeholders={this.config.placeholders}
          placeholder={resolvePlaceholder(
            "property",
            options.defaultTab,
            options.placeholder,
            this.config.placeholders
          )}
          onClose={handleClose}
          onRedirect={handleRedirect}
          prepareNewTab={this.prepareNewTab}
        />
      )
    }

    // The dropdown variant does not self-focus (it renders inline on embedded
    // mounts), so an opened-from-a-trigger dropdown needs the caret moved for it.
    if (variant === "dropdown") {
      requestAnimationFrame(() => mount.querySelector("input")?.focus())
    }

    backdrop.addEventListener("click", (event) => {
      if (!isClickInsideModal(event)) {
        handleClose()
      }
    })

    if (variant === "lightbox") {
      this.releaseScrollLock = lockPageScroll()
    }

    this.active = { host: shadowHost, root, triggerElement }
  }

  /** `immediate` skips the fade — used when another surface is replacing this one. */
  close(immediate = false): void {
    this.epoch += 1
    const active = this.active
    if (!active) {
      this.unlockPage()
      return
    }

    const teardown = () => {
      if (this.active !== active) {
        return
      }
      this.active = null
      this.unlockPage()
      active.root.unmount()
      active.host.destroy()
    }

    if (immediate || prefersReducedMotion() || active.host.host.dataset.bppClosing === "1") {
      active.host.host.dataset.bppClosing = ""
      teardown()
      return
    }

    const node = active.host.host
    node.dataset.bppClosing = "1"
    node.style.opacity = "0"

    let finished = false
    let timer = 0
    const finish = () => {
      if (finished) {
        return
      }
      finished = true
      window.clearTimeout(timer)
      node.removeEventListener("transitionend", onEnd)
      teardown()
    }
    const onEnd = (event: TransitionEvent) => {
      if (event.target !== node || event.propertyName !== "opacity") {
        return
      }
      finish()
    }
    node.addEventListener("transitionend", onEnd)
    timer = window.setTimeout(finish, FADE_MS + 40)
  }

  private unlockPage(): void {
    this.releaseScrollLock?.()
    this.releaseScrollLock = null
    if (this.reservedTab && !this.reservedTab.closed) {
      this.reservedTab.close()
      this.reservedTab = null
    }
  }
}
